// src/LegacyParser.ts
import { logger as logger2 } from "@pexip-engage-public/plugin-utils";
import { decodeURIParameters as legacyDecodeURIParameters } from "skedify-uri-encoding";

// src/LegacyParser.utils.ts
import { logger } from "@pexip-engage-public/plugin-utils";

// src/constants.ts
var DEPRECATED_TOKEN = {
  LOCK: "#",
  OFFICE: "L",
  START: "'"
};
var TOKEN = {
  EMPLOYEE: "E",
  CUSTOMER: "C",
  LOCK: "*",
  MEETING_TYPE: "M",
  OFFICE: "O",
  QUESTION: "Q",
  SUBJECT: "S",
  TIMETABLE: "T"
};

// src/utils.ts
function sanitize(obj) {
  for (const key in obj) {
    if (!obj[key]) {
      delete obj[key];
    }
    if (typeof obj[key] !== "object") {
      continue;
    }
    sanitize(obj[key]);
    if (Object.keys(obj[key]).length === 0) {
      delete obj[key];
    }
  }
  return obj;
}
function encodeURIParameters(obj) {
  const str = JSON.stringify(obj);
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function(_, p1) {
      return String.fromCharCode(parseInt(p1, 16));
    })
  );
}
function decodeURIParameters(str) {
  const result = JSON.parse(
    decodeURIComponent(
      Array.prototype.map.call(atob(str), function(c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      }).join("")
    )
  );
  return sanitize(result);
}
function isValidConfiguration(configuration) {
  return typeof configuration === "object" && configuration !== null;
}

// src/ModernParser.ts
function parse({ element, hash, options }) {
  return {
    domConfig: parseDOMParams(element),
    hashConfig: decodeConfigHash(hash),
    jsConfig: parseJSParams(options)
  };
}
var HASH_PREFIX = "#pexip-";
function encodeConfigAsHash(value) {
  return HASH_PREFIX + encodeURIParameters(value);
}
function decodeConfigHash(hash) {
  if (!hash || !hash.startsWith(HASH_PREFIX))
    return {};
  const value = hash.slice(HASH_PREFIX.length);
  const configuration = decodeURIParameters(value);
  return configuration;
}
var ATTR_PREFIX = "data-pexip-";
function parseDOMParams(element) {
  if (!element)
    return {};
  const attributes = element.getAttributeNames().filter((name) => name.startsWith(ATTR_PREFIX));
  const object = {};
  for (const attribute of attributes) {
    const key = attribute.replace(ATTR_PREFIX, "");
    object[key] = element.getAttribute(attribute)?.trim() || void 0;
  }
  return sanitize(object);
}
function parseJSParams(jsConfig) {
  if (!jsConfig?.config)
    return {};
  const { config } = jsConfig;
  return sanitize(objectToDashConfig(config));
}
function objectToDashConfig(inputObject, current, result = {}) {
  for (let key in inputObject) {
    let value = inputObject[key];
    let newKey = current ? `${current}-${key}` : key;
    if (Array.isArray(value)) {
      result[newKey] = value;
    } else if (value && typeof value === "object") {
      objectToDashConfig(value, newKey, result);
    } else {
      result[newKey] = value;
    }
  }
  return result;
}

// src/LegacyParser.utils.ts
function legacyHashToPluginInputConfiguration(config) {
  return sanitize({
    flow: transformDeprecatedTokens(insertMeetingTypeStepInFlow(config.flo)),
    intent: config.act,
    "subject-ids": config.sub ?? config.esb,
    "subject-type": config.sub ? "id" : config.esb ? "externalId" : void 0,
    "office-ids": config.off ?? config.eof,
    "office-type": config.off ? "id" : config.eof ? "externalId" : void 0,
    "listing-id": config.lis ?? config.eli,
    "listing-type": config.lis ? "id" : config.eli ? "externalId" : void 0,
    "employee-ids": config.con ?? config.eco,
    "employee-type": config.con ? "id" : config.eco ? "externalId" : void 0,
    "lead_segment-id": config.lsc,
    "lead_segment-type": config.lsc ? "code" : void 0,
    "oauth-resource_code": config.rco,
    "session-status": config.cas,
    "session-id": config.sid,
    "session-source_tags": config.sst,
    "customer-company": config.ccom,
    "customer-customer_number": config.ccn,
    "customer-email": config.cem,
    "customer-external_id": config.ceid,
    "customer-first_name": config.cfn,
    "customer-id": config.cid,
    "customer-is_existing": config.cie,
    "customer-language": config.clng,
    "customer-last_name": config.cln,
    "customer-location-city": config.cci,
    "customer-location-country": config.cco,
    "customer-location-geolocation": config.cgl,
    "customer-location-postal_code": config.cpc,
    "customer-location-state": config.csa,
    "customer-location-street_1": config.cst,
    "customer-phone_number": config.cpn,
    "customer-timezone": config.ctz,
    language: config.lng,
    meeting_types: config.mty,
    "application-scroll": config.scr === "disable_initial_scroll_on_schedule" ? "disabled" : void 0,
    "application-timezone_selection": config.ets,
    "application-office_search": void 0,
    "location-initial_search": config.lfa,
    search_countries: config.sco,
    metadata: void 0,
    source_href: void 0,
    "browser-timezone": void 0
  });
}
function logDeprecationWarnings(params) {
  const { dom, hash, js } = createMigrationInstructions(params);
  if (dom || hash || js) {
    logger.group("DEPRECATED CONFIGURATION");
    if (dom) {
      const { migration, unsupported } = dom;
      if (unsupported) {
        logger.warn(`DOM: Following keys are not supported anymore:
${unsupported.join(", ")}`);
      }
      if (migration) {
        logger.warn(
          "Detected unsupported DOM config. Please update your DOM element to:\n%o",
          `<div class="pexip-engage-plugin" ${migration.join(" ")}></div>`
        );
      }
    }
    if (hash) {
      const { migration, unsupported } = hash;
      if (unsupported) {
        logger.warn(`Hash: Following keys are not supported anymore:
${unsupported.join(", ")}`);
      }
      logger.warn("Detected deprecated hash config. Please update your hash to:\n%o", migration);
    }
    if (js) {
      const { migration, unsupported } = js;
      if (unsupported) {
        logger.warn(`JS: Following keys are not supported anymore:
${unsupported.join(", ")}`);
      }
      logger.warn(
        "Detected deprecated JS config. Please update your JS to:\n%o",
        `PexipEngage.Plugin(element, ${JSON.stringify(migration)})`
      );
    }
    logger.groupEnd();
  }
}
function createMigrationInstructions({ dom, hash, js }) {
  return {
    dom: dom && createDOMParamWarnings(dom),
    hash: hash && createHashParamWarnings(hash),
    js: js && createJSParamWarning(js)
  };
}
function createDOMParamWarnings(hashConfig) {
  if (Object.keys(hashConfig).length === 0)
    return null;
  const unsupported = getUnsupportedKeys(hashConfig, "html");
  const config = legacyHashToPluginInputConfiguration(hashConfig);
  const migrations = Object.entries(config).map(([key, value]) => value && `${ATTR_PREFIX}${key}="${value}"`).filter(Boolean);
  const migration = migrations.length === 0 ? null : migrations;
  if (!unsupported && !migration)
    return null;
  return { unsupported, migration };
}
function createHashParamWarnings(hashConfig) {
  if (Object.keys(hashConfig).length === 0)
    return null;
  const unsupported = getUnsupportedKeys(hashConfig, "hash");
  const config = legacyHashToPluginInputConfiguration(hashConfig);
  const modernHash = encodeConfigAsHash(config);
  return { migration: modernHash, unsupported };
}
function createJSParamWarning(hashConfig) {
  if (Object.keys(hashConfig).length === 0)
    return null;
  const unsupported = getUnsupportedKeys(hashConfig, "js");
  const config = legacyHashToPluginInputConfiguration(hashConfig);
  const jsConfig = sanitize({
    config: {
      flow: config.flow,
      intent: config.intent,
      language: config.language,
      employee: {
        ids: config["employee-ids"],
        type: config["employee-type"]
      },
      application: {
        scroll: config["application-scroll"],
        timezoneSelection: config["application-timezone_selection"]
      },
      customer: {
        company: config["customer-company"],
        customer_number: config["customer-customer_number"],
        email: config["customer-email"],
        external_id: config["customer-external_id"],
        first_name: config["customer-first_name"],
        id: config["customer-id"],
        is_existing: config["customer-is_existing"],
        language: config["customer-language"],
        last_name: config["customer-last_name"],
        location: {
          city: config["customer-location-city"],
          country: config["customer-location-country"],
          geolocation: config["customer-location-geolocation"],
          postal_code: config["customer-location-postal_code"],
          state: config["customer-location-state"],
          street_1: config["customer-location-street_1"]
        },
        phone_number: config["customer-phone_number"],
        timezone: config["customer-timezone"]
      },
      leadSegment: {
        id: config["lead_segment-id"],
        type: config["lead_segment-type"]
      },
      listing: {
        id: config["listing-id"],
        type: config["listing-type"]
      },
      location: {
        initialSearch: config["location-initial_search"]
      },
      meetingTypes: config["meeting_types"],
      metadata: config.metadata,
      oauth: {
        resourceCode: config["oauth-resource_code"]
      },
      office: {
        ids: config["office-ids"],
        type: config["office-type"]
      },
      searchCountries: config["search_countries"],
      session: {
        id: config["session-id"],
        sourceTags: config["session-source_tags"],
        status: config["session-status"]
      },
      subject: {
        ids: config["subject-ids"],
        type: config["subject-type"]
      },
      timetable: {}
    }
  });
  return {
    unsupported,
    migration: jsConfig
  };
}
var UNSUPPORTED_HASH_KEY_TO_JS = {
  lgl: "location.geolocation",
  lci: "location.city",
  lco: "location.country",
  lpc: "location.postal_code",
  lsa: "location.state",
  lst: "location.street_1"
};
var UNSUPPORTED_HASH_KEY_TO_HTML = {
  lgl: "skedify:location.geolocation",
  lci: "skedify:location.city",
  lco: "skedify:location.country",
  lpc: "skedify:location.postal_code",
  lsa: "skedify:location.state",
  lst: "skedify:location.street_1"
};
var UNSUPPORTED_KEYS = ["lgl", "lci", "lco", "lpc", "lsa", "lst"];
function getUnsupportedKeys(hashConfig, type) {
  const unsupported = Object.keys(hashConfig).filter(
    (key) => UNSUPPORTED_KEYS.includes(key)
  );
  if (unsupported.length === 0)
    return null;
  if (type === "hash")
    return unsupported;
  if (type === "js")
    return unsupported.map((hash) => UNSUPPORTED_HASH_KEY_TO_JS[hash]);
  if (type === "html")
    return unsupported.map((hash) => UNSUPPORTED_HASH_KEY_TO_HTML[hash]);
  return unsupported;
}
function insertMeetingTypeStepInFlow(flow) {
  if (!flow)
    return void 0;
  const tokens = flow.split("");
  const MEETING_TYPE_IDX = tokens.indexOf(TOKEN.MEETING_TYPE);
  if (MEETING_TYPE_IDX !== -1)
    return flow;
  function getIndexToInsert(tokenIndex) {
    const previousTokenIndex = tokenIndex - 1;
    const isLocked = tokens[previousTokenIndex] === DEPRECATED_TOKEN.LOCK;
    return isLocked ? previousTokenIndex : tokenIndex;
  }
  const OFFICE_IDX = tokens.indexOf(TOKEN.OFFICE);
  const SUBJECT_IDX = tokens.indexOf(TOKEN.SUBJECT);
  if (OFFICE_IDX !== -1) {
    tokens.splice(getIndexToInsert(OFFICE_IDX), 0, TOKEN.MEETING_TYPE);
  } else if (SUBJECT_IDX !== -1) {
    tokens.splice(getIndexToInsert(SUBJECT_IDX), 0, TOKEN.MEETING_TYPE);
  }
  return tokens.join("");
}
function transformDeprecatedTokens(flow) {
  if (!flow)
    return void 0;
  return flow.split("").map((token) => {
    if (token === DEPRECATED_TOKEN.LOCK)
      return TOKEN.LOCK;
    if (token === DEPRECATED_TOKEN.OFFICE)
      return TOKEN.OFFICE;
    if (token === DEPRECATED_TOKEN.START)
      return "";
    return token;
  }).join("");
}

// src/LegacyParser.ts
function parse2({ element, hash, options }) {
  const domConfig = parseDOMParams2(element);
  const hashConfig = parseSkedifyURIHashParams(hash);
  const jsConfig = parseJSConfigParams(options);
  logDeprecationWarnings({ dom: domConfig, hash: hashConfig, js: jsConfig });
  return {
    domConfig: legacyHashToPluginInputConfiguration(domConfig),
    hashConfig: legacyHashToPluginInputConfiguration(hashConfig),
    jsConfig: legacyHashToPluginInputConfiguration(jsConfig)
  };
}
var ATTR_PREFIX2 = "skedify:";
function parseDOMParams2(element) {
  if (!element)
    return {};
  function getAttribute(name) {
    const value = element?.getAttribute(ATTR_PREFIX2 + name)?.trim();
    return value || void 0;
  }
  const configuration = sanitize({
    /** OAUTH */
    rco: getAttribute("oauth.resource_code"),
    // token: getAttribute("oauth.token"),
    /** SESSION */
    cas: getAttribute("session.status"),
    sid: getAttribute("session.id"),
    sst: getAttribute("session.source_tags"),
    /** CUSTOMER */
    ccom: getAttribute("customer.company"),
    ccn: getAttribute("customer.customer_number"),
    cem: getAttribute("customer.email"),
    ceid: getAttribute("customer.external_id"),
    cfn: getAttribute("customer.first_name"),
    cid: getAttribute("customer.id"),
    cie: getAttribute("customer.is_existing"),
    clng: getAttribute("customer.language"),
    cln: getAttribute("customer.last_name"),
    cgl: getAttribute("customer.location.geolocation"),
    cci: getAttribute("customer.location.city"),
    cco: getAttribute("customer.location.country"),
    cpc: getAttribute("customer.location.postal_code"),
    csa: getAttribute("customer.location.state"),
    cst: getAttribute("customer.location.street_1"),
    cpn: getAttribute("customer.phone_number"),
    ctz: getAttribute("customer.timezone"),
    /** APPOINTMENT */
    off: getAttribute("appointment.office_id"),
    sub: getAttribute("appointment.subject_id"),
    lis: getAttribute("appointment.listing_id"),
    /** EXTERNAL */
    esb: getAttribute("external_subject_id"),
    eof: getAttribute("external_office_id"),
    eli: getAttribute("external_listing_id"),
    lsc: getAttribute("lead_segment_code"),
    /** HINTS */
    con: getAttribute("hints.contacts"),
    eco: getAttribute("hints.external_employee_ids"),
    act: getAttribute("intent") ?? getAttribute("hints.intent"),
    lng: getAttribute("language"),
    mty: getAttribute("meeting_types"),
    /** APPLICATION */
    scr: getAttribute("application.scroll"),
    ets: getAttribute("application.timezone_selection"),
    /** LOCATION */
    lfa: getAttribute("location.formatted_address"),
    lgl: getAttribute("location.geolocation"),
    lci: getAttribute("location.city"),
    lco: getAttribute("location.country"),
    lpc: getAttribute("location.postal_code"),
    lsa: getAttribute("location.state"),
    lst: getAttribute("location.street_1"),
    sco: getAttribute("searchCountry") ?? getAttribute("search.country"),
    flo: getAttribute("flow")
  });
  return configuration;
}
var HASH_PREFIX2 = "#skedify";
function parseSkedifyURIHashParams(hash) {
  if (!hash?.startsWith(HASH_PREFIX2))
    return {};
  const encodedParams = hash.slice(HASH_PREFIX2.length);
  try {
    const configuration = sanitize(
      legacyDecodeURIParameters(encodedParams)
    );
    return configuration;
  } catch (err) {
    logger2.error(
      "We could not decode the given configuration. Configuration is ignored.",
      err instanceof Error ? err.message : void 0
    );
    return {};
  }
}
function parseJSConfigParams(config) {
  if (!config)
    return {};
  const configuration = sanitize({
    /** OAUTH */
    rco: config.oauth?.resource_code,
    // token: getAttribute("oauth.token"),
    /** SESSION */
    cas: config.session?.status,
    sid: config.session?.id,
    sst: config.session?.source_tags,
    /** CUSTOMER */
    ccom: config.customer?.company,
    ccn: config.customer?.customer_number,
    cem: config.customer?.email,
    ceid: config.customer?.external_id,
    cfn: config.customer?.first_name,
    cid: config.customer?.id,
    cie: config.customer?.is_existing?.toString(),
    clng: config.customer?.language,
    cln: config.customer?.last_name,
    cgl: config.customer?.location?.geolocation,
    cci: config.customer?.location?.city,
    cco: config.customer?.location?.country,
    cpc: config.customer?.location?.postal_code,
    csa: config.customer?.location?.state,
    cst: config.customer?.location?.street_1,
    cpn: config.customer?.phone_number,
    ctz: config.customer?.timezone,
    /** APPOINTMENT */
    off: config.appointment?.office_id,
    sub: config.appointment?.subject_id,
    lis: config.appointment?.listing_id,
    /** EXTERNAL */
    esb: config.external_subject_id,
    eof: config.external_office_id,
    eli: config.external_listing_id,
    lsc: config.lead_segment_code,
    /** HINTS */
    con: config.hints?.contacts,
    eco: config.hints?.external_employee_ids,
    act: config.intent ?? config.hints?.intent,
    lng: config.language,
    mty: config.meeting_types,
    /** APPLICATION */
    scr: config.application?.scroll,
    ets: config.application?.timezone_selection,
    /** LOCATION */
    lfa: config.location?.formatted_address,
    lgl: config.location?.geolocation,
    lci: config.location?.city,
    lco: config.location?.country,
    lpc: config.location?.postal_code,
    lsa: config.location?.state,
    lst: config.location?.street_1,
    sco: config.searchCountry ?? config.search?.country,
    flo: config.flow
  });
  return configuration;
}

// src/ModernParser.utils.ts
function configToHash(config) {
  return config;
}
function configToHtml(config) {
  return Object.entries(config).map(([key, value]) => `${ATTR_PREFIX}${key}="${value}"`);
}
function configToJS(config) {
  const result = {};
  for (let key in config) {
    const value = config[key];
    const keys = key.split("-");
    setValue(result, keys, value);
  }
  return { config: result };
}
function setValue(target, keys, value) {
  let targetObj = target;
  const [lastKey] = keys.splice(-1, 1);
  for (const key of keys) {
    if (!targetObj[key]) {
      targetObj[key] = {};
    }
    targetObj = targetObj[key];
  }
  targetObj[lastKey] = value;
}

// src/Parser.ts
function parsePluginConfiguration({
  browserTimeZone,
  element,
  hash,
  href,
  options = {}
}) {
  if (!isValidConfiguration(options))
    throw new Error("Invalid configuration passed, expected an object.");
  const legacy = parse2({ element, hash, options });
  const modern = parse({ element, hash, options });
  const domConfig = { ...legacy.domConfig, ...modern.domConfig };
  const hashConfig = { ...legacy.hashConfig, ...modern.hashConfig };
  const jsConfig = { ...legacy.jsConfig, ...modern.jsConfig };
  const config = {
    ...domConfig,
    ...hashConfig,
    ...jsConfig,
    source_href: href,
    "browser-timezone": browserTimeZone
  };
  return { config, domConfig, hashConfig, jsConfig };
}
var CONFIGURATION_SEARCH_PARAM_KEY = "configuration.pexip";
function createSearchParamConfig({ config }) {
  const searchParams = new URLSearchParams();
  searchParams.set(CONFIGURATION_SEARCH_PARAM_KEY, encodeURIParameters(config));
  return searchParams;
}
function parseFromSearchParamConfig(searchParams) {
  const config = searchParams.get(CONFIGURATION_SEARCH_PARAM_KEY);
  if (!config)
    return null;
  return decodeURIParameters(config);
}

// src/index.ts
var legacyUtils = {
  domToLegacyConfig: parseDOMParams2,
  jsToLegacyConfig: parseJSConfigParams,
  hashToLegacyConfig: parseSkedifyURIHashParams,
  legacyConfigToMigrations: createMigrationInstructions,
  legacyConfigToConfig: legacyHashToPluginInputConfiguration
};
var modernUtils = {
  configToJS,
  configToHtml,
  configToHash,
  hashToConfig: decodeConfigHash
};
export {
  DEPRECATED_TOKEN,
  HASH_PREFIX,
  TOKEN,
  createSearchParamConfig,
  decodeConfigHash,
  decodeURIParameters,
  encodeConfigAsHash,
  encodeURIParameters,
  legacyUtils,
  modernUtils,
  parseFromSearchParamConfig,
  parsePluginConfiguration
};
